import { V2d } from "../../../core/Vector";
import { pointInPolygon } from "../../../core/util/Geometry";
import {
  checkSplineIntersection,
  checkSplineSelfIntersection,
  isSplineInsideSpline,
  sampleClosedSpline,
} from "../../../core/util/Spline";
import type { PrecomputedTerrainGPUData } from "../../../editor/io/LevelFileFormat";
import {
  CONTAINMENT_GRID_CELLS,
  CONTAINMENT_GRID_SIZE,
  CONTAINMENT_GRID_U32S_PER_CONTOUR,
  DEFAULT_DEPTH,
  IDW_GRID_CELLS,
  IDW_GRID_CELL_STARTS,
  IDW_GRID_SIZE,
  MAX_IDW_CONTOURS,
  MAX_IDW_DATA,
  SAMPLES_PER_SEGMENT,
} from "./TerrainConstants";

interface BBox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

function computeBBox(points: readonly V2d[]): BBox {
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, minY, maxX, maxY };
}

/** True if inner bbox is fully contained within outer bbox */
function bboxContains(outer: BBox, inner: BBox): boolean {
  return (
    inner.minX >= outer.minX &&
    inner.maxX <= outer.maxX &&
    inner.minY >= outer.minY &&
    inner.maxY <= outer.maxY
  );
}

/**
 * Fast containment check using precomputed bboxes and sampled polygons.
 * Avoids expensive spline resampling and intersection tests.
 */
function isContourInsideContour(
  inner: TerrainContour,
  innerBBox: BBox,
  outer: TerrainContour,
  outerBBox: BBox,
): boolean {
  // Quick rejection: inner bbox must fit inside outer bbox
  if (!bboxContains(outerBBox, innerBBox)) return false;

  // Check if a point from the inner contour is inside the outer polygon.
  // Since we already know the bboxes are nested, if one point is inside
  // and the contours don't intersect, the whole inner contour is inside.
  const testPoint = inner.sampledPolygon[0];
  return (
    testPoint !== undefined && pointInPolygon(testPoint, outer.sampledPolygon)
  );
}

/**
 * A single terrain contour - a closed spline or polygon at a specific height.
 * Contours define elevation levels. The system determines nesting from geometry,
 * allowing flexible configurations like two islands sharing one shelf.
 *
 * - "spline" contours: controlPoints are Catmull-Rom spline control points,
 *   sampledPolygon is the spline sampled at high resolution.
 * - "polygon" contours: controlPoints and sampledPolygon are the same array
 *   of polygon vertices (no spline interpolation).
 */
export interface TerrainContour {
  /** Whether this contour is a spline (Catmull-Rom) or a polygon (direct vertices) */
  readonly type: "spline" | "polygon";

  /** Catmull-Rom control points (spline) or polygon vertices (polygon) */
  readonly controlPoints: readonly V2d[];

  /** Height of this contour in feet (negative = underwater, positive = above water) */
  readonly height: number;

  /** Pre-sampled polygon from Catmull-Rom spline, or the polygon vertices directly */
  readonly sampledPolygon: readonly V2d[];
}

/**
 * Collection of contours defining terrain for a level.
 * Height is determined by which contours a point is inside/outside of.
 */
export interface TerrainDefinition {
  contours: TerrainContour[];
  /** Deep ocean baseline depth in feet (default: DEFAULT_DEPTH) */
  defaultDepth?: number;
  /** Precomputed GPU data from binary .terrain file (v2 format). */
  precomputedGPUData?: PrecomputedTerrainGPUData;
}

/** Number of 32-bit values per contour in GPU buffer (13 values + idwGridDataOffset = 56 bytes) */
export const FLOATS_PER_CONTOUR = 14;

/**
 * A node in the contour containment tree.
 * The tree represents parent-child relationships based on geometric containment.
 */
export interface ContourTreeNode {
  /** Index into the contours array */
  contourIndex: number;
  /** Index of parent node (-1 if this is a root) */
  parentIndex: number;
  /** Depth in the tree (0 = root/directly in ocean) */
  depth: number;
  /** Indices of child nodes (contours directly contained by this one) */
  children: number[];
}

/**
 * Tree structure representing contour containment hierarchy.
 * Used for efficient height calculation - find deepest containing contour,
 * then blend with children using inverse-distance weighting.
 */
export interface ContourTree {
  /** Nodes indexed by contour index */
  nodes: ContourTreeNode[];
  /** Flat array of all child indices for GPU upload */
  childrenFlat: number[];
  /** Maximum depth in the tree */
  maxDepth: number;
}

/**
 * Create a terrain contour.
 * Samples the Catmull-Rom spline at creation time for consistent polygon representation.
 */
export function createContour(
  controlPoints: V2d[],
  height: number,
): TerrainContour {
  return {
    type: "spline",
    controlPoints,
    height,
    sampledPolygon: sampleClosedSpline(
      controlPoints,
      adaptiveSamplesPerSegment(controlPoints),
    ),
  };
}

/**
 * Create a polygon terrain contour (no spline interpolation).
 * The points are used directly as both controlPoints and sampledPolygon.
 */
export function createPolygonContour(
  points: V2d[],
  height: number,
): TerrainContour {
  return {
    type: "polygon",
    controlPoints: points,
    height,
    sampledPolygon: points,
  };
}

/**
 * Choose samples-per-segment based on average segment length.
 * Hand-drawn contours have few, widely-spaced control points and need many
 * interpolation samples for smooth curves. Imported contours have dense points
 * that are already close together and need minimal interpolation.
 */
function adaptiveSamplesPerSegment(controlPoints: V2d[]): number {
  const n = controlPoints.length;
  if (n < 3) return 1;

  let totalLength = 0;
  for (let i = 0; i < n; i++) {
    const a = controlPoints[i];
    const b = controlPoints[(i + 1) % n];
    totalLength += Math.hypot(b.x - a.x, b.y - a.y);
  }

  const avgSegmentLength = totalLength / n;

  // Target roughly one sample per SAMPLES_PER_SEGMENT feet of segment length,
  // clamped to [1, SAMPLES_PER_SEGMENT]
  return Math.max(
    1,
    Math.min(
      SAMPLES_PER_SEGMENT,
      Math.round(avgSegmentLength / SAMPLES_PER_SEGMENT),
    ),
  );
}

/**
 * Compute the signed area of a polygon defined by control points.
 * Uses the shoelace formula.
 *
 * In screen coordinates (Y increases downward):
 * - Positive area = clockwise winding
 * - Negative area = counter-clockwise winding
 *
 * @param points - Polygon vertices (closed loop assumed)
 * @returns Signed area (positive = CW, negative = CCW in screen coords)
 */
export function computeSignedArea(points: readonly V2d[]): number {
  if (points.length < 3) return 0;

  let area = 0;
  const n = points.length;

  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += points[i].x * points[j].y;
    area -= points[j].x * points[i].y;
  }

  return area / 2;
}

/**
 * Check if a contour has counter-clockwise winding (in screen coordinates).
 *
 * For wave physics, CCW winding means:
 * - The interior (land) is on the LEFT as you traverse the contour
 * - The exterior (water) is on the RIGHT
 *
 * @param contour - Terrain contour to check
 * @returns True if the contour has CCW winding
 */
export function isContourCCW(contour: TerrainContour): boolean {
  // In screen coordinates (Y down), negative signed area = CCW
  return computeSignedArea(contour.controlPoints) < 0;
}

/**
 * Ensure a contour has counter-clockwise winding.
 * Returns the contour unchanged if already CCW, or a new contour with reversed points.
 *
 * @param contour - Terrain contour to normalize
 * @returns Contour with CCW winding
 */
export function ensureContourCCW(contour: TerrainContour): TerrainContour {
  if (isContourCCW(contour)) {
    return contour;
  }

  // For polygon contours, controlPoints and sampledPolygon are the same array,
  // so share the reversed array between both fields.
  if (contour.type === "polygon") {
    const reversed = [...contour.controlPoints].reverse();
    return {
      type: "polygon",
      controlPoints: reversed,
      height: contour.height,
      sampledPolygon: reversed,
    };
  }

  // Reverse both control points and sampled polygon to flip winding
  return {
    type: "spline",
    controlPoints: [...contour.controlPoints].reverse(),
    height: contour.height,
    sampledPolygon: [...contour.sampledPolygon].reverse(),
  };
}

/**
 * Normalize all contours in a terrain definition to have CCW winding.
 * This is important for consistent wave shadow computation.
 *
 * @param definition - Terrain definition to normalize
 * @returns New terrain definition with all contours in CCW winding
 */
export function normalizeTerrainWinding(
  definition: TerrainDefinition,
): TerrainDefinition {
  const normalizedContours = definition.contours.map((contour) =>
    ensureContourCCW(contour),
  );

  return {
    contours: normalizedContours,
    defaultDepth: definition.defaultDepth,
    precomputedGPUData: definition.precomputedGPUData,
  };
}

/**
 * Working node structure used during tree construction.
 * Has mutable children array for incremental insertion.
 */
interface WorkingTreeNode {
  contourIndex: number; // -1 for virtual root
  parentIndex: number;
  children: WorkingTreeNode[];
}

/**
 * Build a containment tree from terrain contours using incremental insertion.
 *
 * The tree represents parent-child relationships based on geometric containment:
 * - A contour is a child of another if it's completely inside it
 * - A contour's parent is the smallest contour that contains it
 * - Root contours have no parent (they're directly in the ocean)
 *
 * Algorithm: For each contour, insert it into the tree by:
 * 1. Finding which existing node contains it (recurse into that subtree)
 * 2. If no child contains it, add it as a direct child of current node
 * 3. Reparent any existing children that the new contour contains
 *
 * This avoids the need for size comparisons - containment alone determines hierarchy.
 *
 * @param contours - Array of terrain contours
 * @returns ContourTree with nodes and flattened children array
 */
export function buildContourTree(contours: TerrainContour[]): ContourTree {
  const n = contours.length;

  if (n === 0) {
    return { nodes: [], childrenFlat: [], maxDepth: 0 };
  }

  // Precompute bounding boxes for fast containment rejection
  const bboxes: BBox[] = contours.map((c) => computeBBox(c.sampledPolygon));

  // Virtual root node (represents "ocean" - contains all root-level contours)
  const virtualRoot: WorkingTreeNode = {
    contourIndex: -1,
    parentIndex: -1,
    children: [],
  };

  // Map from contour index to working node for quick lookup
  const nodeMap = new Map<number, WorkingTreeNode>();

  /**
   * Recursively insert a new contour into the tree.
   * @param parent - Current node being examined
   * @param newIndex - Index of the contour to insert
   */
  function insertContour(parent: WorkingTreeNode, newIndex: number): void {
    const newContour = contours[newIndex];
    const newBBox = bboxes[newIndex];

    // Check if any existing child contains the new contour
    for (const child of parent.children) {
      if (
        isContourInsideContour(
          newContour,
          newBBox,
          contours[child.contourIndex],
          bboxes[child.contourIndex],
        )
      ) {
        // New contour is inside this child - recurse deeper
        insertContour(child, newIndex);
        return;
      }
    }

    // No child contains newContour, so it becomes a direct child of parent
    // But first, check if newContour contains any existing children (reparent them)
    const newNode: WorkingTreeNode = {
      contourIndex: newIndex,
      parentIndex: parent.contourIndex,
      children: [],
    };
    nodeMap.set(newIndex, newNode);

    // Find children that should be reparented to the new node
    const childrenToReparent: WorkingTreeNode[] = [];
    for (const child of parent.children) {
      if (
        isContourInsideContour(
          contours[child.contourIndex],
          bboxes[child.contourIndex],
          newContour,
          newBBox,
        )
      ) {
        childrenToReparent.push(child);
      }
    }

    // Reparent contained children to newNode
    for (const child of childrenToReparent) {
      // Remove from parent's children
      const idx = parent.children.indexOf(child);
      if (idx >= 0) {
        parent.children.splice(idx, 1);
      }
      // Add to new node's children
      newNode.children.push(child);
      child.parentIndex = newIndex;
    }

    // Add newNode as child of parent
    parent.children.push(newNode);
  }

  // Insert all contours one by one
  for (let i = 0; i < n; i++) {
    insertContour(virtualRoot, i);
  }

  // Convert working tree to final ContourTreeNode array
  const nodes: ContourTreeNode[] = contours.map((_, i) => ({
    contourIndex: i,
    parentIndex: -1,
    depth: 0,
    children: [],
  }));

  // Copy parent/child relationships from working tree
  for (let i = 0; i < n; i++) {
    const workingNode = nodeMap.get(i)!;
    nodes[i].parentIndex = workingNode.parentIndex;
    nodes[i].children = workingNode.children.map((c) => c.contourIndex);
  }

  // Compute depths via BFS from roots
  const roots = nodes.filter((node) => node.parentIndex === -1);
  let maxDepth = 0;

  const queue: number[] = roots.map((n) => n.contourIndex);
  while (queue.length > 0) {
    const idx = queue.shift()!;
    const node = nodes[idx];
    const depth = node.parentIndex >= 0 ? nodes[node.parentIndex].depth + 1 : 0;
    node.depth = depth;
    maxDepth = Math.max(maxDepth, depth);

    for (const childIdx of node.children) {
      queue.push(childIdx);
    }
  }

  // Build flat children array for GPU
  const childrenFlat: number[] = [];
  for (const node of nodes) {
    for (const childIdx of node.children) {
      childrenFlat.push(childIdx);
    }
  }

  return { nodes, childrenFlat, maxDepth };
}

// Track which terrain definitions have been validated to avoid duplicate warnings
const validatedDefinitions = new WeakSet<TerrainDefinition>();

/**
 * Validate terrain definition and log warnings for potential issues.
 * Checks for:
 * - Self-intersecting contours (spline segments that cross themselves)
 * - Contours that intersect each other
 *
 * Only validates each definition once to avoid log spam.
 *
 * TODO: Actually call this function when loading terrain definitions to provide
 * better error messages during development.
 */
export function validateTerrainDefinition(definition: TerrainDefinition): void {
  // Skip if already validated
  if (validatedDefinitions.has(definition)) return;
  validatedDefinitions.add(definition);

  const contours = definition.contours;
  if (contours.length === 0) return;

  // Check each contour for self-intersection (spline contours only —
  // polygon contours are validated by the offline validator instead)
  for (let i = 0; i < contours.length; i++) {
    const contour = contours[i];
    if (contour.type === "polygon") continue;

    if (contour.controlPoints.length < 3) {
      console.warn(
        `Terrain contour ${i} at height ${contour.height} has only ${contour.controlPoints.length} control points`,
      );
      continue;
    }

    const selfIntersections = checkSplineSelfIntersection(
      contour.controlPoints,
    );
    if (selfIntersections.length > 0) {
      const first = selfIntersections[0];
      console.warn(
        `Terrain contour ${i} at height ${contour.height} self-intersects. ` +
          `Found ${selfIntersections.length} intersection(s). ` +
          `First at (${first.point.x.toFixed(0)}, ${first.point.y.toFixed(0)}) ` +
          `between segments ${first.segmentA} and ${first.segmentB}.`,
      );
    }
  }

  // Check all pairs of contours for intersection (spline contours only)
  for (let i = 0; i < contours.length; i++) {
    for (let j = i + 1; j < contours.length; j++) {
      const contourA = contours[i];
      const contourB = contours[j];

      if (contourA.type === "polygon" || contourB.type === "polygon") continue;

      if (
        contourA.controlPoints.length < 3 ||
        contourB.controlPoints.length < 3
      ) {
        continue;
      }

      const intersections = checkSplineIntersection(
        contourA.controlPoints,
        contourB.controlPoints,
      );

      if (intersections.length > 0) {
        const first = intersections[0];
        console.warn(
          `Terrain contours ${i} (height ${contourA.height}) and ${j} (height ${contourB.height}) intersect. ` +
            `Found ${intersections.length} intersection(s). ` +
            `First at (${first.point.x.toFixed(0)}, ${first.point.y.toFixed(0)}).`,
        );
      }
    }
  }
}

// =============================================================================
// Containment Grid — O(1) point-in-contour for GPU queries
// =============================================================================

const CELL_OUTSIDE = 0;
const CELL_INSIDE = 1;
const CELL_BOUNDARY = 2;

/**
 * DDA line rasterization through grid cells, marking each traversed cell as BOUNDARY.
 * Ported from Rust terrain.rs `rasterize_edge_to_grid`.
 */
function rasterizeEdgeToGrid(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  minX: number,
  minY: number,
  invCellW: number,
  invCellH: number,
  cols: number,
  rows: number,
  cellFlags: Uint8Array,
): void {
  const maxCol = cols - 1;
  const maxRow = rows - 1;

  let col = Math.floor((ax - minX) * invCellW);
  let row = Math.floor((ay - minY) * invCellH);
  const endCol = Math.floor((bx - minX) * invCellW);
  const endRow = Math.floor((by - minY) * invCellH);

  // Mark start cell
  if (col >= 0 && col <= maxCol && row >= 0 && row <= maxRow) {
    cellFlags[row * cols + col] = CELL_BOUNDARY;
  }

  const dx = bx - ax;
  const dy = by - ay;

  const stepCol = dx > 0 ? 1 : dx < 0 ? -1 : 0;
  const stepRow = dy > 0 ? 1 : dy < 0 ? -1 : 0;

  const cellW = 1 / invCellW;
  const cellH = 1 / invCellH;

  let tMaxX =
    dx !== 0
      ? ((dx > 0 ? minX + (col + 1) * cellW : minX + col * cellW) - ax) / dx
      : Infinity;
  let tMaxY =
    dy !== 0
      ? ((dy > 0 ? minY + (row + 1) * cellH : minY + row * cellH) - ay) / dy
      : Infinity;

  const tDeltaX = dx !== 0 ? Math.abs(cellW / dx) : Infinity;
  const tDeltaY = dy !== 0 ? Math.abs(cellH / dy) : Infinity;

  const maxSteps = (cols + rows) * 2;
  for (let step = 0; step < maxSteps; step++) {
    if (col === endCol && row === endRow) break;

    if (tMaxX < tMaxY) {
      col += stepCol;
      tMaxX += tDeltaX;
    } else {
      row += stepRow;
      tMaxY += tDeltaY;
    }

    if (col >= 0 && col <= maxCol && row >= 0 && row <= maxRow) {
      cellFlags[row * cols + col] = CELL_BOUNDARY;
    }
  }
}

/**
 * Build a 64x64 containment grid for a contour's sampled polygon.
 * Returns a Uint32Array of 256 elements with 2-bit packed flags
 * (OUTSIDE=0, INSIDE=1, BOUNDARY=2).
 *
 * Algorithm (ported from Rust terrain.rs):
 * 1. DDA-rasterize each polygon edge to mark BOUNDARY cells
 * 2. Test center of each non-BOUNDARY cell with pointInPolygon
 * 3. Pack into 2-bit representation (16 cells per u32)
 */
function buildContainmentGrid(
  polygon: readonly V2d[],
  bboxMinX: number,
  bboxMinY: number,
  bboxMaxX: number,
  bboxMaxY: number,
): Uint32Array {
  const cols = CONTAINMENT_GRID_SIZE;
  const rows = CONTAINMENT_GRID_SIZE;
  const numCells = CONTAINMENT_GRID_CELLS;

  const w = Math.max(bboxMaxX - bboxMinX, 1e-9);
  const h = Math.max(bboxMaxY - bboxMinY, 1e-9);
  const invCellW = cols / w;
  const invCellH = rows / h;
  const cellW = w / cols;
  const cellH = h / rows;

  const cellFlags = new Uint8Array(numCells);
  // cellFlags starts as all CELL_OUTSIDE (0)

  // Rasterize polygon edges to mark BOUNDARY cells
  const n = polygon.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    rasterizeEdgeToGrid(
      polygon[i].x,
      polygon[i].y,
      polygon[j].x,
      polygon[j].y,
      bboxMinX,
      bboxMinY,
      invCellW,
      invCellH,
      cols,
      rows,
      cellFlags,
    );
  }

  // Test center of non-BOUNDARY cells with pointInPolygon
  const testPoint = new V2d(0, 0);
  for (let cell = 0; cell < numCells; cell++) {
    if (cellFlags[cell] !== CELL_BOUNDARY) {
      const col = cell % cols;
      const row = Math.floor(cell / cols);
      testPoint.x = bboxMinX + (col + 0.5) * cellW;
      testPoint.y = bboxMinY + (row + 0.5) * cellH;
      if (pointInPolygon(testPoint, polygon)) {
        cellFlags[cell] = CELL_INSIDE;
      }
    }
  }

  // Pack into 2-bit representation: 16 cells per u32
  const packed = new Uint32Array(CONTAINMENT_GRID_U32S_PER_CONTOUR);
  for (let i = 0; i < numCells; i++) {
    packed[i >> 4] |= (cellFlags[i] & 3) << ((i & 15) * 2);
  }

  return packed;
}

// =============================================================================
// IDW Grid — precomputed candidate edges for GPU IDW blending
// =============================================================================

/** Squared distance from point to segment (no sqrt). */
function pointToSegmentDistSq(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  const abx = bx - ax;
  const aby = by - ay;
  const lenSq = Math.max(abx * abx + aby * aby, 1e-20);
  const t = Math.max(
    0,
    Math.min(1, ((px - ax) * abx + (py - ay) * aby) / lenSq),
  );
  const nx = ax + t * abx;
  const ny = ay + t * aby;
  const dx = px - nx;
  const dy = py - ny;
  return dx * dx + dy * dy;
}

/** Squared distance from point to axis-aligned rectangle (0 if inside). */
function pointToRectDistSq(
  px: number,
  py: number,
  rx0: number,
  ry0: number,
  rx1: number,
  ry1: number,
): number {
  const dx = px < rx0 ? rx0 - px : px > rx1 ? px - rx1 : 0;
  const dy = py < ry0 ? ry0 - py : py > ry1 ? py - ry1 : 0;
  return dx * dx + dy * dy;
}

/** Test if segment (ax,ay)-(bx,by) intersects axis-aligned rectangle. */
function segmentIntersectsRect(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  rx0: number,
  ry0: number,
  rx1: number,
  ry1: number,
): boolean {
  if (ax >= rx0 && ax <= rx1 && ay >= ry0 && ay <= ry1) return true;
  if (bx >= rx0 && bx <= rx1 && by >= ry0 && by <= ry1) return true;

  const ddx = bx - ax;
  const ddy = by - ay;
  let tMin = 0;
  let tMax = 1;

  const clips: [number, number][] = [
    [-ddx, ax - rx0],
    [ddx, rx1 - ax],
    [-ddy, ay - ry0],
    [ddy, ry1 - ay],
  ];

  for (const [p, q] of clips) {
    if (Math.abs(p) < 1e-20) {
      if (q < 0) return false;
    } else {
      const r = q / p;
      if (p < 0) {
        if (r > tMax) return false;
        if (r > tMin) tMin = r;
      } else {
        if (r < tMin) return false;
        if (r < tMax) tMax = r;
      }
    }
  }
  return tMin <= tMax;
}

/** Min squared distance from axis-aligned rect to segment (0 if they intersect). */
function minDistSqRectSegment(
  rx0: number,
  ry0: number,
  rx1: number,
  ry1: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  if (segmentIntersectsRect(ax, ay, bx, by, rx0, ry0, rx1, ry1)) return 0;

  let d = Infinity;
  d = Math.min(d, pointToSegmentDistSq(rx0, ry0, ax, ay, bx, by));
  d = Math.min(d, pointToSegmentDistSq(rx1, ry0, ax, ay, bx, by));
  d = Math.min(d, pointToSegmentDistSq(rx0, ry1, ax, ay, bx, by));
  d = Math.min(d, pointToSegmentDistSq(rx1, ry1, ax, ay, bx, by));
  d = Math.min(d, pointToRectDistSq(ax, ay, rx0, ry0, rx1, ry1));
  d = Math.min(d, pointToRectDistSq(bx, by, rx0, ry0, rx1, ry1));
  return d;
}

/** Max squared distance from any rect point to segment (at a corner, since
 *  dist-to-segment is convex). */
function maxDistSqRectSegment(
  rx0: number,
  ry0: number,
  rx1: number,
  ry1: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  return Math.max(
    pointToSegmentDistSq(rx0, ry0, ax, ay, bx, by),
    pointToSegmentDistSq(rx1, ry0, ax, ay, bx, by),
    pointToSegmentDistSq(rx0, ry1, ax, ay, bx, by),
    pointToSegmentDistSq(rx1, ry1, ax, ay, bx, by),
  );
}

/**
 * Build a 16×16 IDW candidate grid for a parent contour and its children.
 * Each grid cell stores candidate (tag, edgeIndex) pairs for edges that could
 * be the nearest boundary for any point in that cell.
 *
 * Returns null if the grid would be too large.
 */
function buildIDWGrid(
  parentPolygon: readonly V2d[],
  childPolygons: readonly (readonly V2d[])[],
  parentBBox: BBox,
): { cellStarts: Uint32Array; entries: Uint32Array } | null {
  const minX = parentBBox.minX;
  const minY = parentBBox.minY;
  const w = Math.max(parentBBox.maxX - minX, 1e-9);
  const h = Math.max(parentBBox.maxY - minY, 1e-9);
  const cellW = w / IDW_GRID_SIZE;
  const cellH = h / IDW_GRID_SIZE;
  // Collect all edges with tags
  interface IDWEdge {
    ax: number;
    ay: number;
    bx: number;
    by: number;
    tag: number;
    edgeIndex: number;
  }

  const edges: IDWEdge[] = [];

  // Parent edges (tag 0)
  for (let i = 0; i < parentPolygon.length; i++) {
    const j = (i + 1) % parentPolygon.length;
    edges.push({
      ax: parentPolygon[i].x,
      ay: parentPolygon[i].y,
      bx: parentPolygon[j].x,
      by: parentPolygon[j].y,
      tag: 0,
      edgeIndex: i,
    });
  }

  // Child edges (tags 1..N)
  for (let ci = 0; ci < childPolygons.length; ci++) {
    const poly = childPolygons[ci];
    for (let i = 0; i < poly.length; i++) {
      const j = (i + 1) % poly.length;
      edges.push({
        ax: poly[i].x,
        ay: poly[i].y,
        bx: poly[j].x,
        by: poly[j].y,
        tag: ci + 1,
        edgeIndex: i,
      });
    }
  }

  // For each cell, find candidate edges using exact rect-to-segment distances.
  // Per-tag upper bound = min over same-tag edges of max_dist(cell_rect, edge).
  // Edge E is included if min_dist(cell_rect, E) ≤ upper bound for its tag.
  const numTags = 1 + childPolygons.length;
  const cellEntries: number[][] = new Array(IDW_GRID_CELLS);
  let totalEntries = 0;
  const perTagUpperBoundSq = new Float64Array(numTags);

  for (let row = 0; row < IDW_GRID_SIZE; row++) {
    for (let col = 0; col < IDW_GRID_SIZE; col++) {
      const rx0 = minX + col * cellW;
      const ry0 = minY + row * cellH;
      const rx1 = rx0 + cellW;
      const ry1 = ry0 + cellH;

      // Find per-tag upper bound: smallest max-distance to any edge of that tag
      perTagUpperBoundSq.fill(Infinity);
      for (const e of edges) {
        const dMax = maxDistSqRectSegment(
          rx0,
          ry0,
          rx1,
          ry1,
          e.ax,
          e.ay,
          e.bx,
          e.by,
        );
        if (dMax < perTagUpperBoundSq[e.tag]) perTagUpperBoundSq[e.tag] = dMax;
      }

      const cell = row * IDW_GRID_SIZE + col;
      // Collect candidates passing the rect-segment upper bound filter
      const candidateIndices: number[] = [];
      for (let i = 0; i < edges.length; i++) {
        const e = edges[i];
        const dMin = minDistSqRectSegment(
          rx0,
          ry0,
          rx1,
          ry1,
          e.ax,
          e.ay,
          e.bx,
          e.by,
        );
        if (dMin <= perTagUpperBoundSq[e.tag]) {
          candidateIndices.push(i);
        }
      }

      // Add all candidates that pass the rect-segment upper bound filter.
      const candidates: number[] = candidateIndices.map((i) => {
        const e = edges[i];
        return (e.tag << 16) | e.edgeIndex;
      });

      cellEntries[cell] = candidates;
      totalEntries += candidates.length;
    }
  }

  // Build prefix-sum cell_starts and flat entries
  const cellStarts = new Uint32Array(IDW_GRID_CELL_STARTS);
  cellStarts[0] = 0;
  for (let i = 0; i < IDW_GRID_CELLS; i++) {
    cellStarts[i + 1] = cellStarts[i] + cellEntries[i].length;
  }

  const entries = new Uint32Array(totalEntries);
  let writeIdx = 0;
  for (let i = 0; i < IDW_GRID_CELLS; i++) {
    for (const packed of cellEntries[i]) {
      entries[writeIdx++] = packed;
    }
  }

  return { cellStarts, entries };
}

/**
 * Build GPU data arrays from terrain definition.
 * Returns flat arrays ready for upload to GPU buffers.
 *
 * Pre-samples Catmull-Rom splines on the CPU at high resolution (SAMPLES_PER_SEGMENT
 * samples per control point segment). This produces smooth polygon vertices that
 * the GPU can iterate directly without needing to evaluate splines.
 *
 * Contours are reordered in DFS pre-order for efficient GPU traversal. Each contour
 * has a skipCount indicating how many contours are in its subtree, allowing the GPU
 * to skip entire subtrees when a point is outside a contour.
 *
 * IMPORTANT: The WGSL struct has u32 fields for pointStartIndex and pointCount,
 * so we need to use a DataView to write integers with correct bit patterns.
 */
export function buildTerrainGPUData(definition: TerrainDefinition): {
  vertexData: Float32Array;
  contourData: ArrayBuffer;
  childrenData: Uint32Array;
  containmentGridData: Uint32Array;
  idwGridData: Uint32Array;
  lookupGridData: Uint32Array;
  coastlineIndices: Uint32Array;
  contourCount: number;
  coastlineCount: number;
  maxDepth: number;
  defaultDepth: number;
} {
  const contours = definition.contours;

  if (contours.length === 0) {
    return {
      vertexData: new Float32Array(0),
      contourData: new ArrayBuffer(0),
      childrenData: new Uint32Array(0),
      containmentGridData: new Uint32Array(0),
      idwGridData: new Uint32Array(0),
      lookupGridData: new Uint32Array(0),
      coastlineIndices: new Uint32Array(0),
      contourCount: 0,
      coastlineCount: 0,
      maxDepth: 0,
      defaultDepth: definition.defaultDepth ?? DEFAULT_DEPTH,
    };
  }

  // Build containment tree
  const tree = buildContourTree(contours);

  // Compute DFS ordering and skip counts
  // dfsOrder[i] = original contour index at DFS position i
  // originalToDfs[originalIndex] = DFS position
  // skipCounts[dfsPosition] = number of descendants (subtree size)
  const dfsOrder: number[] = [];
  const originalToDfs = new Map<number, number>();
  const skipCounts: number[] = new Array(contours.length).fill(0);

  // DFS traversal to build ordering and compute skip counts
  function dfsVisit(originalIndex: number): number {
    const dfsIndex = dfsOrder.length;
    dfsOrder.push(originalIndex);
    originalToDfs.set(originalIndex, dfsIndex);

    // Visit children and accumulate subtree size
    const node = tree.nodes[originalIndex];
    let subtreeSize = 0;
    for (const childOriginalIndex of node.children) {
      subtreeSize += 1 + dfsVisit(childOriginalIndex);
    }

    // Store skip count at the correct DFS index (not push, since we're in post-order here)
    skipCounts[dfsIndex] = subtreeSize;
    return subtreeSize;
  }

  // Start DFS from root nodes (those with parentIndex === -1)
  for (let i = 0; i < tree.nodes.length; i++) {
    if (tree.nodes[i].parentIndex === -1) {
      dfsVisit(i);
    }
  }

  // Use pre-sampled polygons from contours
  const sampledContours = contours.map((contour) => contour.sampledPolygon);

  // Count total vertices after sampling
  let totalVertices = 0;
  for (const vertices of sampledContours) {
    totalVertices += vertices.length;
  }

  const vertexData = new Float32Array(totalVertices * 2);

  // Use ArrayBuffer + DataView to write mixed u32/f32 data correctly
  // Layout per contour (56 bytes = 14 x 4 bytes):
  //   0-3:   pointStartIndex (u32) - refers to pre-sampled vertices
  //   4-7:   pointCount (u32) - refers to pre-sampled vertices
  //   8-11:  height (f32)
  //   12-15: parentIndex (i32, -1 if root) - in DFS indices
  //   16-19: depth (u32)
  //   20-23: childStartIndex (u32)
  //   24-27: childCount (u32)
  //   28-31: isCoastline (u32, 1 if height == 0)
  //   32-35: bboxMinX (f32)
  //   36-39: bboxMinY (f32)
  //   40-43: bboxMaxX (f32)
  //   44-47: bboxMaxY (f32)
  //   48-51: skipCount (u32) - number of contours in subtree
  //   52-55: idwGridDataOffset (u32) - absolute offset into packed buffer for IDW grid
  const contourBuffer = new ArrayBuffer(
    contours.length * FLOATS_PER_CONTOUR * 4,
  );
  const contourView = new DataView(contourBuffer);

  // Build flat children index array in DFS order
  // childStartIndices[dfsIndex] = start index in children buffer
  const childStartIndices: number[] = [];
  const childrenFlat: number[] = [];
  for (let dfsIndex = 0; dfsIndex < dfsOrder.length; dfsIndex++) {
    const originalIndex = dfsOrder[dfsIndex];
    const node = tree.nodes[originalIndex];
    childStartIndices.push(childrenFlat.length);
    // Store children as DFS indices
    for (const childOriginalIndex of node.children) {
      childrenFlat.push(originalToDfs.get(childOriginalIndex)!);
    }
  }
  const childrenData = new Uint32Array(childrenFlat);

  // Collect coastline indices (height == 0 contours) in DFS order
  const coastlineIndicesList: number[] = [];
  for (let dfsIndex = 0; dfsIndex < dfsOrder.length; dfsIndex++) {
    const originalIndex = dfsOrder[dfsIndex];
    if (contours[originalIndex].height === 0) {
      coastlineIndicesList.push(dfsIndex);
    }
  }
  const coastlineIndices = new Uint32Array(coastlineIndicesList);

  // Write contour data in DFS order, and build containment grids
  const containmentGridData = new Uint32Array(
    contours.length * CONTAINMENT_GRID_U32S_PER_CONTOUR,
  );
  let vertexIndex = 0;
  for (let dfsIndex = 0; dfsIndex < dfsOrder.length; dfsIndex++) {
    const originalIndex = dfsOrder[dfsIndex];
    const contour = contours[originalIndex];
    const node = tree.nodes[originalIndex];
    const vertices = sampledContours[originalIndex];

    // Store contour metadata - byte offset for each contour
    const byteBase = dfsIndex * FLOATS_PER_CONTOUR * 4;

    // u32 fields (must use setUint32, not float)
    contourView.setUint32(byteBase + 0, vertexIndex, true); // pointStartIndex
    contourView.setUint32(byteBase + 4, vertices.length, true); // pointCount

    // f32 field
    contourView.setFloat32(byteBase + 8, contour.height, true);

    // Tree structure fields - parentIndex in DFS indices
    const parentDfsIndex =
      node.parentIndex === -1 ? -1 : originalToDfs.get(node.parentIndex)!;
    contourView.setInt32(byteBase + 12, parentDfsIndex, true); // parentIndex (signed)
    contourView.setUint32(byteBase + 16, node.depth, true); // depth
    contourView.setUint32(byteBase + 20, childStartIndices[dfsIndex], true); // childStartIndex
    contourView.setUint32(byteBase + 24, node.children.length, true); // childCount
    contourView.setUint32(byteBase + 28, contour.height === 0 ? 1 : 0, true); // isCoastline

    // Compute bounding box from pre-sampled vertices
    let minX = 0,
      maxX = 0,
      minY = 0,
      maxY = 0;
    if (vertices.length > 0) {
      minX = Infinity;
      maxX = -Infinity;
      minY = Infinity;
      maxY = -Infinity;
      for (const pt of vertices) {
        minX = Math.min(minX, pt.x);
        maxX = Math.max(maxX, pt.x);
        minY = Math.min(minY, pt.y);
        maxY = Math.max(maxY, pt.y);
      }
      contourView.setFloat32(byteBase + 32, minX, true); // bboxMinX
      contourView.setFloat32(byteBase + 36, minY, true); // bboxMinY
      contourView.setFloat32(byteBase + 40, maxX, true); // bboxMaxX
      contourView.setFloat32(byteBase + 44, maxY, true); // bboxMaxY
    }

    // Skip count for DFS traversal
    contourView.setUint32(byteBase + 48, skipCounts[dfsIndex], true); // skipCount

    // IDW grid data offset (0 = no grid, set later if grid is built)
    contourView.setUint32(byteBase + 52, 0, true); // idwGridDataOffset

    // Build containment grid for this contour
    if (vertices.length >= 3) {
      const grid = buildContainmentGrid(vertices, minX, minY, maxX, maxY);
      const gridOffset = dfsIndex * CONTAINMENT_GRID_U32S_PER_CONTOUR;
      containmentGridData.set(grid, gridOffset);
    }

    // Store pre-sampled vertices
    for (const pt of vertices) {
      vertexData[vertexIndex * 2 + 0] = pt.x;
      vertexData[vertexIndex * 2 + 1] = pt.y;
      vertexIndex++;
    }
  }

  // Build IDW grids for contours with children
  const idwGridParts: Uint32Array[] = [];
  let idwGridTotalSize = 0;

  // Count eligible contours (have children and fit IDW limit) for progress reporting
  let eligibleCount = 0;
  for (const originalIndex of dfsOrder) {
    const node = tree.nodes[originalIndex];
    if (
      node.children.length > 0 &&
      node.children.length + 1 <= MAX_IDW_CONTOURS
    ) {
      eligibleCount++;
    }
  }

  let processedCount = 0;
  for (let dfsIndex = 0; dfsIndex < dfsOrder.length; dfsIndex++) {
    const originalIndex = dfsOrder[dfsIndex];
    const node = tree.nodes[originalIndex];
    const contour = contours[originalIndex];

    if (
      node.children.length === 0 ||
      node.children.length + 1 > MAX_IDW_CONTOURS
    ) {
      continue;
    }

    // Gather parent polygon
    const parentPoly = contour.sampledPolygon;
    const parentBBox = computeBBox(parentPoly);

    // Gather child polygons
    const childPolygons: (readonly V2d[])[] = [];
    for (const childOrigIdx of node.children) {
      childPolygons.push(contours[childOrigIdx].sampledPolygon);
    }

    const grid = buildIDWGrid(parentPoly, childPolygons, parentBBox);
    if (grid === null) continue;

    const gridSize = grid.cellStarts.length + grid.entries.length;
    if (idwGridTotalSize + gridSize > MAX_IDW_DATA) {
      const pct = ((processedCount / eligibleCount) * 100).toFixed(1);
      const usedMB = ((idwGridTotalSize * 4) / (1024 * 1024)).toFixed(1);
      const budgetMB = ((MAX_IDW_DATA * 4) / (1024 * 1024)).toFixed(1);
      console.warn(
        `IDW grid data exceeds budget (${usedMB}/${budgetMB} MB), ` +
          `processed ${processedCount}/${eligibleCount} contours (${pct}%)`,
      );
      break;
    }

    if (gridSize > 100000) {
      const totalEdges =
        parentPoly.length + childPolygons.reduce((s, p) => s + p.length, 0);
      console.log(
        `IDW grid: ${((gridSize * 4) / 1024).toFixed(0)} KB, ` +
          `${totalEdges} edges, ${childPolygons.length} children, ` +
          `${grid.entries.length} entries (${(grid.entries.length / totalEdges).toFixed(1)}x duplication)`,
      );
    }

    processedCount++;

    // Record relative offset + 1 (so 0 remains "no grid" sentinel)
    const byteBase = dfsIndex * FLOATS_PER_CONTOUR * 4;
    contourView.setUint32(byteBase + 52, idwGridTotalSize + 1, true);

    idwGridParts.push(grid.cellStarts);
    idwGridParts.push(grid.entries);
    idwGridTotalSize += gridSize;
  }

  // Assemble flat idwGridData
  const idwGridData = new Uint32Array(idwGridTotalSize);
  let idwWriteOffset = 0;
  for (const part of idwGridParts) {
    idwGridData.set(part, idwWriteOffset);
    idwWriteOffset += part.length;
  }

  return {
    vertexData,
    contourData: contourBuffer,
    childrenData,
    containmentGridData,
    idwGridData,
    lookupGridData: new Uint32Array(0),
    coastlineIndices,
    contourCount: contours.length,
    coastlineCount: coastlineIndices.length,
    maxDepth: tree.maxDepth,
    defaultDepth: definition.defaultDepth ?? DEFAULT_DEPTH,
  };
}

/**
 * Build GPU data from precomputed binary terrain data.
 * Extracts coastlineIndices and maxDepth from contour metadata,
 * passes arrays through without recomputation.
 */
export function buildTerrainGPUDataFromPrecomputed(
  precomputed: PrecomputedTerrainGPUData,
): {
  vertexData: Float32Array;
  contourData: ArrayBuffer;
  childrenData: Uint32Array;
  containmentGridData: Uint32Array;
  idwGridData: Uint32Array;
  lookupGridData: Uint32Array;
  coastlineIndices: Uint32Array;
  contourCount: number;
  coastlineCount: number;
  maxDepth: number;
  defaultDepth: number;
} {
  const contourView = new DataView(precomputed.contourData);
  const coastlineList: number[] = [];
  let maxDepth = 0;

  for (let i = 0; i < precomputed.contourCount; i++) {
    const base = i * FLOATS_PER_CONTOUR * 4;
    const height = contourView.getFloat32(base + 8, true);
    const depth = contourView.getUint32(base + 16, true);
    if (height === 0) {
      coastlineList.push(i);
    }
    if (depth > maxDepth) {
      maxDepth = depth;
    }
  }

  return {
    vertexData: precomputed.vertexData,
    contourData: precomputed.contourData,
    childrenData: precomputed.childrenData,
    containmentGridData: precomputed.containmentGridData,
    idwGridData: precomputed.idwGridData,
    lookupGridData: precomputed.lookupGridData,
    coastlineIndices: new Uint32Array(coastlineList),
    contourCount: precomputed.contourCount,
    coastlineCount: coastlineList.length,
    maxDepth,
    defaultDepth: precomputed.defaultDepth,
  };
}
